import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import {
  FaUserShield, FaClock, FaExclamationTriangle, FaInfoCircle, FaFolderOpen, FaPlay
} from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';   // shared design system
import './Reviews.css';

const STATUS_LABEL = {
  open:      'Open',
  completed: 'Completed',
  skipped:   'Skipped'
};
const TIER_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };
const OUTCOMES = [
  { v: 'no_change',           label: 'No change' },
  { v: 'risk_tier_changed',   label: 'Risk tier changed' },
  { v: 'edd_required',        label: 'EDD required' },
  { v: 'escalated',           label: 'Escalated to regulator' },
  { v: 'customer_offboarded', label: 'Customer offboarded' }
];

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${dt.getFullYear()}`;
};

const fmt = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}, ${hh}:${mi}`;
};

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const customerName = (r) =>
  r.customer_name_individual || r.customer_name_business || `Customer #${r.customer_id}`;

const isOverdue = (r) =>
  r.status === 'open' && r.due_at && new Date(r.due_at) < new Date(new Date().toDateString());

function ManageReviews() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const canRead     = hasPermission(user, 'customer_reviews.read');
  const canComplete = hasPermission(user, 'customer_reviews.complete');
  const canSweep    = hasPermission(user, 'customer_reviews.run_sweep');

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ status: 'open', overdue: false });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [completeForm, setCompleteForm] = useState(null);
  const [completeBusy, setCompleteBusy] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepResult, setSweepResult] = useState(null);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get('/customer-reviews/stats'); setStats(r.data); }
    catch (e) { console.error(e.message); }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      if (filter.status !== 'all') p.set('status', filter.status);
      if (filter.overdue)          p.set('overdue', 'true');
      p.set('limit', '500');
      const r = await axios.get(`/customer-reviews?${p.toString()}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!canRead) return;
    fetchStats();
    fetchList();
  }, [canRead, fetchStats, fetchList]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const r = await axios.get(`/customer-reviews/${id}`);
      setDetail(r.data);
    } catch (e) {
      setError(e.message || 'Failed to load review');
      setSelectedId(null);
    }
  };

  const runSweepNow = async () => {
    setSweepBusy(true); setError(null); setSweepResult(null);
    try {
      const r = await axios.post('/customer-reviews/run-sweep');
      setSweepResult(r.data);
      fetchStats(); fetchList();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Sweep failed');
    } finally {
      setSweepBusy(false);
    }
  };

  const submitComplete = async () => {
    if (!completeForm || !selectedId) return;
    setCompleteBusy(true); setError(null);
    try {
      const payload = { outcome: completeForm.outcome };
      if (completeForm.outcome === 'risk_tier_changed' && completeForm.new_risk_tier) {
        payload.new_risk_tier = completeForm.new_risk_tier;
      }
      if (completeForm.notes && completeForm.notes.trim()) payload.notes = completeForm.notes.trim();
      await axios.post(`/customer-reviews/${selectedId}/complete`, payload);
      setCompleteForm(null);
      const r = await axios.get(`/customer-reviews/${selectedId}`);
      setDetail(r.data);
      fetchList(); fetchStats();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Failed to complete review');
    } finally {
      setCompleteBusy(false);
    }
  };

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to view Periodic CDD reviews.
            Ask your administrator for the <code>customer_reviews.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Stat cards */}
      {user && (
        <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--teal">
              <div className="stat-label">Total reviews</div>
              <div className="stat-value stat-value--teal">{stats ? stats.total : '—'}</div>
            </div>
            <div className="stat-card stat-card--blue">
              <div className="stat-label">Open</div>
              <div className="stat-value stat-value--blue">{stats ? stats.open : '—'}</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">Overdue</div>
              <div className="stat-value stat-value--orange">{stats ? stats.overdue : '—'}</div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Due in 30 days</div>
              <div className="stat-value stat-value--purple">{stats ? stats.due_soon : '—'}</div>
            </div>
            <div className="stat-card stat-card--brown">
              <div className="stat-label">Completed (30d)</div>
              <div className="stat-value">{stats ? stats.completed_last_30d : '—'}</div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Filters + actions */}
      <section className="form-section" style={{ marginBottom: '1rem' }}>
        <header className="form-section__head">
          <h4 className="form-section__title">Review queue</h4>
          <p className="form-section__subtitle">Periodic CDD reviews — re-KYC cadence by risk tier (high: 6mo, medium: 12mo, low: 24mo).</p>
        </header>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Status</label>
            <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}>
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="completed">Completed</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
          <div className="form-group">
            <label>&nbsp;</label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, textTransform: 'none' }}>
              <input
                type="checkbox"
                checked={filter.overdue}
                onChange={e => setFilter({ ...filter, overdue: e.target.checked })}
              />
              Overdue only
            </label>
          </div>
          <div className="form-group">
            <label>&nbsp;</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="export-button" onClick={fetchList} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
              {canSweep && (
                <button className="export-button" onClick={runSweepNow} disabled={sweepBusy}>
                  <FaPlay /> {sweepBusy ? 'Sweeping…' : 'Run sweep now'}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {sweepResult && (
        <div className="alert-banner alert-banner--success" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> Sweep complete: {sweepResult.swept_customers} customer(s) processed,
            {' '}{sweepResult.reviews_created} review(s) opened, {sweepResult.cases_created} case(s) created.
            {sweepResult.errors?.length > 0 && ` ${sweepResult.errors.length} error(s).`}
          </span>
        </div>
      )}

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* List */}
      <div className="review-list">
        {rows.length === 0 && !loading && (
          <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No reviews match the current filters.</p>
        )}
        {rows.map(r => {
          const overdue = isOverdue(r);
          const tier = r.risk_tier_at_due || 'low';
          return (
            <div key={r.id}
                 className={`review-card tier-${tier}${overdue ? ' is-overdue' : ''}${r.status === 'completed' ? ' is-completed' : ''}`}
                 onClick={() => openDetail(r.id)}>
              <div className="review-card__top">
                <div style={{ flex: 1 }}>
                  <h4 className="review-card__title">Periodic CDD review — {TIER_LABEL[tier]} risk</h4>
                  <div className="review-card__customer">
                    <FaUserShield /> {customerName(r)}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-end' }}>
                  <span className={`review-pill review-pill--tier-${tier}`}>{TIER_LABEL[tier]}</span>
                  <span className={`review-pill review-pill--status-${r.status}`}>{STATUS_LABEL[r.status]}</span>
                  {overdue && <span className="review-pill review-pill--overdue"><FaExclamationTriangle /> Overdue</span>}
                </div>
              </div>
              <div className="review-card__meta">
                <span><FaClock /> Due {fmtDate(r.due_at)}</span>
                {r.completed_at && <span>Completed {fmtDate(r.completed_at)}</span>}
                {r.case_number && <span><FaFolderOpen /> {r.case_number}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail modal */}
      {selectedId && (
        <div className="modal-overlay" onClick={() => { setSelectedId(null); setCompleteForm(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <button className="modal-close" onClick={() => { setSelectedId(null); setCompleteForm(null); }}>×</button>
            {!detail ? <div>Loading…</div> : (
              <>
                <h2 style={{ marginTop: 0 }}>Periodic CDD review #{detail.id}</h2>
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className={`review-pill review-pill--tier-${detail.risk_tier_at_due || 'low'}`}>
                    {TIER_LABEL[detail.risk_tier_at_due || 'low']} risk
                  </span>
                  <span className={`review-pill review-pill--status-${detail.status}`}>{STATUS_LABEL[detail.status]}</span>
                  {detail.case_number && (
                    <button className="btn-cancel" style={{ padding: '0.2rem 0.55rem', fontSize: '0.7rem' }}
                            onClick={() => navigate(`/cases?id=${detail.case_id}`)}>
                      <FaFolderOpen /> {detail.case_number}
                    </button>
                  )}
                </div>

                <section className="form-section">
                  <header className="form-section__head">
                    <h4 className="form-section__title">Customer</h4>
                  </header>
                  <div className="form-grid-2">
                    <div><strong>Name:</strong> {customerName(detail)}</div>
                    <div><strong>Type:</strong> {detail.customer_type || '—'}</div>
                    <div><strong>Risk tier at scheduling:</strong> {detail.risk_tier_at_due || '—'}</div>
                    <div><strong>Current risk tier:</strong> {detail.current_risk_tier || '—'}</div>
                    <div><strong>Due:</strong> {fmtDate(detail.due_at)}</div>
                    {detail.completed_at && <div><strong>Completed:</strong> {fmt(detail.completed_at)} by {detail.completed_by_email || '—'}</div>}
                  </div>
                </section>

                {detail.status === 'completed' && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Outcome</h4>
                    </header>
                    <p style={{ margin: 0 }}>
                      <strong>{detail.outcome || '—'}</strong>
                      {detail.new_risk_tier && <> → new risk tier: <strong>{detail.new_risk_tier}</strong></>}
                    </p>
                    {detail.notes && <p style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' }}>{detail.notes}</p>}
                  </section>
                )}

                {canComplete && detail.status === 'open' && !completeForm && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="export-button" onClick={() => setCompleteForm({ outcome: 'no_change', new_risk_tier: '', notes: '' })}>
                      Complete review
                    </button>
                  </div>
                )}

                {completeForm && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Complete review</h4>
                      <p className="form-section__subtitle">Outcome advances the next review date by the customer's (possibly updated) risk-tier interval.</p>
                    </header>
                    <div className="form-grid-2">
                      <div className="form-group">
                        <label>Outcome</label>
                        <select value={completeForm.outcome}
                                onChange={e => setCompleteForm(f => ({ ...f, outcome: e.target.value }))}>
                          {OUTCOMES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                      </div>
                      {completeForm.outcome === 'risk_tier_changed' && (
                        <div className="form-group">
                          <label>New risk tier</label>
                          <select value={completeForm.new_risk_tier}
                                  onChange={e => setCompleteForm(f => ({ ...f, new_risk_tier: e.target.value }))}>
                            <option value="">— pick one —</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                      )}
                      <div className="form-group form-group--full">
                        <label>Notes (optional)</label>
                        <textarea rows={3}
                                  value={completeForm.notes}
                                  onChange={e => setCompleteForm(f => ({ ...f, notes: e.target.value }))} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button className="btn-cancel" disabled={completeBusy} onClick={() => setCompleteForm(null)}>Cancel</button>
                      <button className="export-button" disabled={completeBusy} onClick={submitComplete}>
                        {completeBusy ? 'Saving…' : 'Confirm'}
                      </button>
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageReviews;
